import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Site } from '@/core/entities';
import SiteForm from '@/features/sites/site-form/SiteForm';
import { useSites } from '@/queries/sites';
import { CustomerContext } from '../../index/Customer';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting, Toolbar, Item } from 'devextreme-react/data-grid';

const SitesPage: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { customer } = useOutletContext<CustomerContext>();

    const [sites, setSites] = useState<Site[]>([]);

    const {
        queryKey: sitesQueryKey,
        query: { isLoading, data: sitePages },
    } = useSites({
        customerId: customer!.id,
        queryParams: { limit: 99999 },
    });

    useEffect(() => {
        if (sitePages) {
            setSites(sitePages.pages[0].data);
        }
    }, [sitePages]);

    const [siteForm, setSiteForm] = useState({ open: false });

    const refetch = () => {
        /* eslint-disable @typescript-eslint/no-floating-promises */
        queryClient.invalidateQueries(sitesQueryKey);
    };

    if (isLoading) return <Backdrop />;

    return (
        <>
            <Stack direction="column" spacing={3}>
                <DataGrid
                    dataSource={sites}
                    showBorders={true}
                    columnAutoWidth={true}
                    rowAlternationEnabled={false}
                    allowColumnResizing={true}
                    allowColumnReordering={true}
                    hoverStateEnabled={true}
                    onRowClick={(e) => {
                        if (e.data) {
                            navigate(`/customers/${customer!.id}/sites/${(e.data as Site).id}`);
                        }
                    }}
                >
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <HeaderFilter visible={true} />
                    <SearchPanel visible={true} highlightCaseSensitive={true} />
                    <Paging defaultPageSize={50} enabled={true} />
                    <Toolbar>
                        <Item location="before">
                            <Button
                                variant="contained"
                                startIcon={<AddRounded />}
                                size="large"
                                onClick={() => setSiteForm({ ...siteForm, open: true })}
                            >
                                Create site
                            </Button>
                        </Item>
                        <Item name="searchPanel" location="after" />
                    </Toolbar>

                    <Column dataField="name" caption="Site" />
                    <Column
                        caption="Address, city"
                        calculateCellValue={(rowData: Site) =>
                            [rowData.address, rowData.city].filter(Boolean).join(', ') || '-'
                        }
                    />
                    <Column
                        caption="Main point of contact"
                        calculateCellValue={(rowData: Site) =>
                            [rowData.siteAdminName, rowData.siteAdminTitle, rowData.siteAdminEmail]
                                .filter(Boolean)
                                .join(' • ') || '-'
                        }
                    />
                    <Column dataField="timeZone" caption="Time zone" />
                    <Column dataField="numberOfDevices" caption="No. of devices" alignment="right" />
                </DataGrid>
            </Stack>
            <SiteForm
                open={siteForm.open}
                onClose={() => setSiteForm({ open: false })}
                customer={customer}
                onSubmitted={refetch}
                subtitle={customer?.name}
            />
        </>
    );
};

export default SitesPage;
